import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Bell,
  ChevronDown,
  Settings,
  LogOut,
  HelpCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  Clock,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ALERTS, ALERT_COLORS } from '@/lib/mockData'
import { formatDate } from '@/lib/utils'
import type { AlertLevel } from '@/types'

// ----------------------------------------------------------
// Alert level helpers (P1-P4 canonical scale)
// ----------------------------------------------------------

function alertDot(level: AlertLevel): string {
  const map: Record<AlertLevel, string> = {
    P1: 'bg-emerald-400',
    P2: 'bg-amber-400',
    P3: 'bg-orange-400',
    P4: 'bg-red-400',
  }
  return map[level]
}

function alertIconComponent(level: AlertLevel) {
  if (level === 'P4' || level === 'P3') return AlertTriangle
  if (level === 'P2') return Info
  return CheckCircle
}

// ----------------------------------------------------------
// Notification Panel
// ----------------------------------------------------------

interface NotificationPanelProps {
  onClose: () => void
}

function NotificationPanel({ onClose }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  const unresolved = ALERTS.filter((a) => a.status !== 'resolved')

  return (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className={cn(
        'absolute top-full right-0 mt-2 w-96 z-50',
        'glass-strong rounded-xl overflow-hidden shadow-2xl'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/60">
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-terra-blue" />
          <span className="text-sm font-semibold text-white">Notifiche</span>
          {unresolved.length > 0 && (
            <span className="text-xs font-bold bg-terra-red/20 text-terra-red border border-terra-red/30 rounded-full px-2 py-0.5">
              {unresolved.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-slate-500 hover:text-slate-300 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* Alert list */}
      <div className="max-h-80 overflow-y-auto">
        {ALERTS.map((alert) => {
          const Icon = alertIconComponent(alert.level)
          const levelColor = ALERT_COLORS[alert.level]
          return (
            <div
              key={alert.id}
              className={cn(
                'px-4 py-3 border-b border-slate-800/40 hover:bg-slate-800/40',
                'transition-colors cursor-pointer',
                alert.status === 'resolved' && 'opacity-50'
              )}
            >
              <div className="flex items-start gap-3">
                <div className={cn('mt-0.5 flex-shrink-0 w-2 h-2 rounded-full', alertDot(alert.level))} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-200 leading-tight">
                      {alert.title}
                    </p>
                    <Icon
                      size={13}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: levelColor }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">
                    {alert.municipality}, {alert.province}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Clock size={10} className="text-slate-600" />
                    <span className="text-xs text-slate-600">
                      {formatDate(alert.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-slate-700/60">
        <button className="w-full text-xs text-terra-blue hover:text-sky-400 transition-colors py-1">
          Vedi tutte le allerte
        </button>
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// User Menu
// ----------------------------------------------------------

interface UserMenuProps {
  onClose: () => void
}

function UserMenu({ onClose }: UserMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  return (
    <motion.div
      ref={menuRef}
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className="absolute top-full right-0 mt-2 w-52 z-50 glass-strong rounded-xl overflow-hidden shadow-2xl"
    >
      {/* User info */}
      <div className="px-4 py-3 border-b border-slate-700/60">
        <p className="text-sm font-semibold text-white">Gianluca Capone</p>
        <p className="text-xs text-slate-400 truncate">gcapo@wheretech.it</p>
        <span className="mt-1 inline-block text-xs bg-terra-purple/20 text-terra-purple border border-terra-purple/30 rounded px-2 py-0.5">
          Admin
        </span>
      </div>

      {/* Menu items */}
      <div className="py-1">
        {[
          { icon: Settings, label: 'Impostazioni' },
          { icon: HelpCircle, label: 'Supporto' },
        ].map(({ icon: Icon, label }) => (
          <button
            key={label}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800/60 transition-colors"
          >
            <Icon size={14} className="text-slate-500" />
            {label}
          </button>
        ))}
      </div>

      <div className="border-t border-slate-700/60 py-1">
        <button className="w-full flex items-center gap-3 px-4 py-2 text-sm text-terra-red/80 hover:text-terra-red hover:bg-terra-red/10 transition-colors">
          <LogOut size={14} />
          Logout
        </button>
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// TopBar
// ----------------------------------------------------------

export default function TopBar() {
  const [notifOpen, setNotifOpen] = useState(false)
  const [userOpen, setUserOpen] = useState(false)
  const [searchFocused, setSearchFocused] = useState(false)

  // Unread = active, not yet acknowledged
  const unreadCount = ALERTS.filter((a) => a.status === 'active').length

  return (
    <header className={cn(
      'h-14 flex items-center justify-between px-6 flex-shrink-0',
      'glass-strong border-b border-slate-800/60 z-20'
    )}>
      {/* Left: search */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className={cn(
          'relative flex items-center gap-2 h-8 w-64 rounded-lg',
          'bg-slate-800/60 border transition-all duration-200',
          searchFocused
            ? 'border-terra-blue/50 shadow-[0_0_0_2px_rgba(14,165,233,0.15)]'
            : 'border-slate-700/60 hover:border-slate-600/60'
        )}>
          <Search size={13} className={cn(
            'ml-3 flex-shrink-0 transition-colors',
            searchFocused ? 'text-terra-blue' : 'text-slate-500'
          )} />
          <input
            type="text"
            placeholder="Cerca area, bacino, comune..."
            className="flex-1 bg-transparent text-xs text-slate-300 placeholder:text-slate-600 outline-none pr-3"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* System status */}
        <div className="hidden md:flex items-center gap-1.5 px-3 py-1 rounded-lg bg-terra-green/10 border border-terra-green/20 text-xs text-terra-green font-medium mr-2">
          <span className="w-1.5 h-1.5 rounded-full bg-terra-green animate-pulse-slow" />
          Sistema Operativo
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              setNotifOpen((o) => !o)
              setUserOpen(false)
            }}
            className={cn(
              'relative w-8 h-8 flex items-center justify-center rounded-lg',
              'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60',
              'transition-all duration-200',
              notifOpen && 'bg-slate-800/80 text-terra-blue'
            )}
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-terra-red text-white text-[9px] font-bold flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          <AnimatePresence>
            {notifOpen && (
              <NotificationPanel onClose={() => setNotifOpen(false)} />
            )}
          </AnimatePresence>
        </div>

        {/* User avatar */}
        <div className="relative">
          <button
            onClick={() => {
              setUserOpen((o) => !o)
              setNotifOpen(false)
            }}
            className={cn(
              'flex items-center gap-2 h-8 px-2 rounded-lg',
              'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60',
              'transition-all duration-200',
              userOpen && 'bg-slate-800/80 text-slate-200'
            )}
          >
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-terra-blue to-terra-purple flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              G
            </div>
            <span className="hidden md:block text-xs font-medium text-slate-300 max-w-20 truncate">
              gcapo
            </span>
            <ChevronDown
              size={12}
              className={cn(
                'hidden md:block transition-transform duration-200',
                userOpen && 'rotate-180'
              )}
            />
          </button>

          <AnimatePresence>
            {userOpen && (
              <UserMenu onClose={() => setUserOpen(false)} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  )
}
